import EventsTracking from '../../../shared/eventsTracking.js';
import VideoPreview from '../../../shared/videoPreview.js';
import { IntersectionWatcher } from '../../../shared/intersectionWatcher.js';
import { trapFocus } from '../../../shared/utils/dom/focus.js';

const VIDEO_PREVIEW_MAX_SECONDS = 5;

const CSS_CLASS_SHOW = 'show';
const CSS_CLASS_ANIMATION_ACTIVE = 'animated--active';
const AMP_STORY_PLAYER_SELECTOR = 'amp-story-player';
const ANIMATED_SELECTOR = '.animated';
const RA_AMP_PLAYER_SELECTOR = '.ra-amp__player';
const RA_AMP_CLOSE_SELECTOR = '.ra-amp__close';
const RA_AMP_SELECTOR = '.ra-amp';
const RA_AMP_STORY_LINKS_WRAPPER_SELECTOR = '.ra-amp__stories';
const RA_AMP_STORY_LINK_SELECTOR = '.ra-amp__story-link';
const RA_AMP_STORY_LABEL_SELECTOR = '.ra-amp__storyLabel';

export class AmpStoryPlayer {
  isStoryRewindedByStoryIndex = {};
  deactivateFocusTrap = null;
  isPlayerOpened = false;

  constructor({ el, target, storyPageId }) {
    this.target = target;
    this.storyPageId = storyPageId;
    this.player = el.querySelector(AMP_STORY_PLAYER_SELECTOR);
    this.playerContainer = el.querySelector(RA_AMP_PLAYER_SELECTOR);
    this.closeButton = el.querySelector(RA_AMP_CLOSE_SELECTOR);
    this.storiesWrapper = el.querySelector(RA_AMP_SELECTOR);
    this.storyLinksWrapper = el.querySelector(RA_AMP_STORY_LINKS_WRAPPER_SELECTOR);
    this.taggingId = this.storiesWrapper.dataset?.refappTaggingId;
    this.links = el.querySelectorAll(RA_AMP_STORY_LINK_SELECTOR);
    this.videos = Array.from(el.querySelectorAll('video'));

    this.intersectionWatcher = new IntersectionWatcher({
      threshold: 0.25,
    });
  }

  init() {
    this.initEventsTracking();
    this.closeButton.addEventListener('click', () => this.closePlayer());
    document.addEventListener('keydown', (e) => {
      if (this.isPlayerOpened && e.key === 'Escape') {
        this.closePlayer();
      }
    });
    this.player.addEventListener('ready', () => this.onPlayerReady());
    this.initOpenPlayerOnPage();
    this.setupVideoPreviews();
    this.initAnimation();
    this.setAnchorTitle();
  }

  setAnchorTitle() {
    const labelsDOMCollection = document.querySelectorAll(RA_AMP_STORY_LABEL_SELECTOR);
    if (labelsDOMCollection.length > 0) {
      labelsDOMCollection.forEach(label => {
        label.setAttribute('title', label.textContent);
      })
    }
  }

  initEventsTracking() {
    if (this.taggingId) {
      const clickTrackerOptions = { capture: false, once: false };
      new EventsTracking(this.taggingId, { clickTrackerOptions });
    }
  }

  showPlayer() {
    this.isPlayerOpened = true;
    this.playerContainer.inert = false;
    this.deactivateFocusTrap = trapFocus(this.playerContainer);

    this.player.play();
    document.body.classList.add('no-scroll');

    // timeout prevents showing horizontal scrolling of amp stories to user
    setTimeout(() => {
      this.playerContainer.classList.add(CSS_CLASS_SHOW);
    }, 200);
  }

  closePlayer() {
    if (!this.isPlayerOpened) {
      return;
    }

    this.isPlayerOpened = false;
    this.playerContainer.inert = true;
    this.deactivateFocusTrap();
    this.deactivateFocusTrap = null;

    this.player.pause();
    document.body.classList.remove('no-scroll');
    this.playerContainer.classList.remove(CSS_CLASS_SHOW);
  }

  onPlayerReady() {
    this.player.addEventListener('noNextStory', () => {
      setTimeout(() => this.closePlayer(), 1000);
    });

    this.player.addEventListener('navigation', (e) => {
      this.handleNavigationBetweenStoriesEvent(e);
    });
  }

  handleNavigationBetweenStoriesEvent(e) {
    const currStoryIndex = e.detail.index;
    if (this.isStoryRewindedByStoryIndex[currStoryIndex]) return;

    this.isStoryRewindedByStoryIndex[currStoryIndex] = true;

    // without timeout there is a higher chance that rewinding
    // could cause some internal bug in amp and story would freeze and/or not render correctly
    setTimeout(() => {
      this.player.rewind(); // works better than player.show() in this case
    }, 50);
  }

  initOpenPlayerOnPage() {
    if (this.target === 'tab') return;

    this.links.forEach((link, index) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.player.show(link.href, this.storyPageId);
        this.showPlayer();
        this.isStoryRewindedByStoryIndex = {
          [index]: true,
        };
      });
    });
  }

  async setupVideoPreviews() {
    if (!this.videos.length) return;

    const tasks = this.videos.map((video) => {
      const videoPreview = VideoPreview.generatePreview(video.dataset.src)
        .then((blobUrl) => ({ video, blobUrl }))
        .catch((error) => console.error('Video preview error:', error));

      let videoPoster = Promise.resolve();
      if (!video.poster) {
        videoPoster = VideoPreview.generatePoster(video.dataset.src)
          .then((dataUrl) => {
            if (dataUrl) {
              video.poster = dataUrl;
            }
          })
          .catch((error) => console.error('Video poster error:', error));
      }

      return Promise.all([videoPreview, videoPoster]);
    });

    const results = await Promise.all(tasks);

    await this.intersectionWatcher.intersecting(this.storyLinksWrapper);

    results.forEach((item) => {
      if (item?.[0]?.blobUrl) {
        const video = item[0].video;
        video.src = item[0].blobUrl;
        this.setupResetForVideo(video);
      }
    });
  }

  /**
   * resets the video to initial state so that it shows only poster
   */
  setupResetForVideo(video) {
    let isReset = false;

    const reset = () => {
      if (isReset) return;
      isReset = true;

      video.pause();
      video.removeAttribute('src');
      video.load();
    };

    const onTimeUpdate = () => {
      if (video.currentTime >= VIDEO_PREVIEW_MAX_SECONDS) {
        reset();
      }
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', reset);
  }

  async initAnimation() {
    await this.intersectionWatcher.intersecting(this.storyLinksWrapper);
    this.storyLinksWrapper.querySelectorAll(ANIMATED_SELECTOR).forEach((animatedStory) => {
      animatedStory.classList.add(CSS_CLASS_ANIMATION_ACTIVE);
    });
  }
}
